/**
 * Per-module LLM override inference.
 *
 * When the user picks a `scoring_model` for a module that crosses providers
 * (e.g. picking `bedrock/moonshotai.kimi-k2.5` on an Anthropic-top-level
 * pipeline), constantine's per-pipeline provider resolver routes the chat()
 * call to `/anthropic/v1/messages` and bifrost rejects with 500 `no keys
 * found that support model/deployment: kimi-k2.5`.
 *
 * The fix is editor-side: emit a per-module `llm:` override block on the
 * ModuleRefObject so constantine's runtime three-layer merge
 * (internal/pipeline/runner.go ~L420) applies it as a module-scoped override.
 * Contract pinned by constantine-agent (msg #136) — UI emits the block;
 * runner consumes it; no SDK change needed.
 *
 * This module exports the pure inference function used by the editor's
 * setModuleConfig callback when a `scoring_model` knob is set.
 */

import type { ModelTier, PipelineLLM } from './types';

export interface ModuleLlmOverride {
  provider: string;
  models: Partial<Record<ModelTier, string>>;
  budget?: number;
}

/**
 * Heuristic: does `model` belong to a different provider than `topLevelProvider`?
 *
 * Rules (matches the existing premium-bug-rush preset's deltascan-scanner
 * block — same pattern):
 *   - Any `/`-prefixed routed SKU (bedrock/*, openai/*, openrouter/*,
 *     self-hosted/*, azure_ai/*, databricks/*) → litellm provider.
 *   - Anthropic regional/Bedrock spellings (`us.anthropic.*`, `us.amazon.*`,
 *     `bedrock/...`) → litellm.
 *   - `claude-opus-4-7` is Bedrock-only today; Anthropic-direct cannot serve
 *     it. Treat it as cross-provider when top-level is anthropic.
 *   - Bare `claude-*` names on Anthropic top-level → no override (direct).
 */
function isCrossProviderModel(
  topLevelProvider: string | undefined,
  model: string
): boolean {
  // litellm already accepts every cross-provider routed string; never needs override.
  if (topLevelProvider === 'litellm') return false;

  // Any provider-prefixed routed SKU implies a non-anthropic provider.
  if (model.includes('/')) return true;

  // Regional Bedrock spellings (no slash, but still cross-provider).
  if (model.startsWith('us.anthropic.')) return true;
  if (model.startsWith('us.amazon.')) return true;
  if (model.startsWith('amazon.')) return true;
  if (model.startsWith('moonshotai.')) return true;
  if (model.startsWith('deepseek.')) return true;
  if (model.startsWith('mistral.')) return true;
  if (model.startsWith('zai.')) return true;
  if (model.startsWith('google.')) return true;
  if (model === 'kimi-k2.5') return true;

  // claude-opus-4-7 is Bedrock-only today. Anthropic-direct can't serve it —
  // see modelPricing.ts header for source-of-truth notes.
  if (model === 'claude-opus-4-7') {
    return topLevelProvider === 'anthropic';
  }

  return false;
}

/**
 * Infer a per-module `llm:` override block for a `scoring_model` selection.
 * Returns null when no override is needed (same-provider selection, missing
 * input, or top-level pipeline doesn't require routing).
 *
 * The block pins the cross-provider model across all three tiers (fast /
 * standard / deep) since constantine's chat() call may pick any tier at
 * runtime; locking all three to the same SKU keeps behavior deterministic
 * regardless of which tier the module's cost_model declares.
 */
export function inferModuleLlmOverride(
  topLevelLlm: PipelineLLM | undefined,
  scoringModel: string | undefined | null
): ModuleLlmOverride | null {
  if (!topLevelLlm) return null;
  if (!scoringModel) return null;
  if (!isCrossProviderModel(topLevelLlm.provider, scoringModel)) return null;

  // NOTE: deliberately does NOT copy the top-level `budget` onto the module
  // override. Constantine treats a per-module `llm` block as all-or-nothing
  // and self-limits the module against whatever `budget` it carries, so a
  // baked-in (and stale) per-module budget would cap the module below the
  // authoritative per-scan account budget. The override exists for PROVIDER
  // + MODEL routing only; budget is a pipeline-level concept and is
  // reconciled authoritatively by the backend.
  const override: ModuleLlmOverride = {
    provider: 'litellm',
    models: {
      fast: scoringModel,
      standard: scoringModel,
      deep: scoringModel,
    },
  };
  return override;
}
